import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import moment from 'moment'
import Swal from 'sweetalert2'
import { AuthContext } from '@/context/auth-context'
import AvailabilityService from '@/services/AvailabilityService'
import MojeProjektyService from '@/services/MojeProjektyService'
import MojeSzkoleniaService from '@/services/MojeSzkoleniaService'
import { buildProjectCalendarItems, filterTrainingsByRange, getMonthLabel } from '../../common/availabilityUtils'

const availabilityIdOf = item => `${item?._id || item?.id || ''}`

export default function useDyspozycjaList() {
	const { i18n, t } = useTranslation()
	const navigate = useNavigate()
	const { user } = useContext(AuthContext)
	const userId = user?._id || user?.id || user?.userid || ''

	const [startDate, setStartDate] = useState(() => moment().startOf('month'))
	const [showDatePicker, setShowDatePicker] = useState(false)
	const [tempDate, setTempDate] = useState(() => moment().startOf('month').format('YYYY-MM-DD'))
	const [availability, setAvailability] = useState([])
	const [selectedIds, setSelectedIds] = useState([])
	const [deleting, setDeleting] = useState(false)
	const [trainings, setTrainings] = useState([])
	const [projects, setProjects] = useState([])
	const [loading, setLoading] = useState(true)
	const [availabilityError, setAvailabilityError] = useState('')
	const [trainingsError, setTrainingsError] = useState('')
	const [projectsError, setProjectsError] = useState('')

	const datePickerRef = useRef(null)
	const inputRef = useRef(null)

	const monthRange = useMemo(() => ({
		start: startDate.clone().startOf('month').format('YYYY-MM-DD'),
		end: startDate.clone().endOf('month').format('YYYY-MM-DD'),
	}), [startDate])

	const dateDisplay = getMonthLabel(startDate, i18n)

	const loadData = useCallback(async () => {
		setLoading(true)
		setAvailabilityError('')
		setTrainingsError('')
		setProjectsError('')

		const [availabilityResult, trainingsResult, projectsResult] = await Promise.all([
			AvailabilityService.getMyAvailability({
				startDate: monthRange.start,
				endDate: monthRange.end,
				limit: 100,
				sortColumn: 'startDate',
				sortDirection: 'asc',
			}),
			MojeSzkoleniaService.getAll({
				limit: 200,
				sortColumn: 'data_rozpoczecia',
				sortDirection: 'asc',
			}),
			userId
				? MojeProjektyService.getAll({
					limit: 200,
					sortColumn: 'created',
					sortDirection: 'desc',
					filters: { trainerId: userId },
				})
				: Promise.resolve({ data: [], error: '' }),
		])

		const nextAvailability = availabilityResult.error ? [] : availabilityResult.data
		setAvailability(nextAvailability)
		setSelectedIds(prev => {
			const visibleIds = new Set(nextAvailability.map(availabilityIdOf).filter(Boolean))
			return prev.filter(id => visibleIds.has(id))
		})
		setAvailabilityError(availabilityResult.error || '')
		setTrainings(
			trainingsResult.error
				? []
				: filterTrainingsByRange(trainingsResult.data, monthRange.start, monthRange.end)
		)
		setTrainingsError(trainingsResult.error || '')
		setProjects(
			projectsResult.error
				? []
				: filterTrainingsByRange(
					buildProjectCalendarItems(projectsResult.data, userId),
					monthRange.start,
					monthRange.end,
				)
		)
		setProjectsError(projectsResult.error || '')
		setLoading(false)
	}, [monthRange.end, monthRange.start, userId])

	useEffect(() => {
		loadData()
	}, [loadData])

	useEffect(() => {
		setTempDate(startDate.format('YYYY-MM-DD'))
	}, [startDate])

	useEffect(() => {
		const handleClickOutside = event => {
			if (datePickerRef.current && !datePickerRef.current.contains(event.target)) {
				setShowDatePicker(false)
			}
		}

		document.addEventListener('mousedown', handleClickOutside)
		return () => document.removeEventListener('mousedown', handleClickOutside)
	}, [])

	const handlePrevious = useCallback(() => {
		setStartDate(prev => prev.clone().subtract(1, 'month'))
	}, [])

	const handleNext = useCallback(() => {
		setStartDate(prev => prev.clone().add(1, 'month'))
	}, [])

	const handleToday = useCallback(() => {
		setStartDate(moment().startOf('month'))
	}, [])

	const handleDateChange = useCallback(newDate => {
		setStartDate(moment(newDate).startOf('month'))
	}, [])

	const handleDatePickerChange = useCallback(event => {
		const newDate = event.target.value
		setTempDate(newDate)
		if (newDate) {
			handleDateChange(newDate)
			setShowDatePicker(false)
		}
	}, [handleDateChange])

	const handleDatePickerBlur = useCallback(() => {
		setTimeout(() => setShowDatePicker(false), 200)
	}, [])

	const handleToggleDatePicker = useCallback(() => {
		setShowDatePicker(prev => !prev)
	}, [])

	const handleAdd = useCallback(() => {
		navigate('/centrumSzkolen/dyspozycja/add', { state: { anchorDate: startDate.toISOString() } })
	}, [navigate, startDate])

	const handleAddForDate = useCallback(date => {
		if (!date) return
		navigate('/centrumSzkolen/dyspozycja/add', { state: { anchorDate: date } })
	}, [navigate])

	const handleToggleSelect = useCallback(availabilityId => {
		const id = `${availabilityId || ''}`
		if (!id) return
		setSelectedIds(prev => (
			prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
		))
	}, [])

	const handleDeleteSelected = useCallback(async () => {
		if (!selectedIds.length || deleting) return

		const confirm = await Swal.fire({
			title: t('notifications.confirmTitle'),
			text: t('trainingAvailability.list.deleteSelectedConfirm', { count: selectedIds.length }),
			icon: 'warning',
			showCancelButton: true,
			confirmButtonColor: '#d33',
			confirmButtonText: t('notifications.confirmDeleteButton'),
			cancelButtonText: t('notifications.cancelButton'),
		})
		if (!confirm.isConfirmed) return

		setDeleting(true)
		const results = await Promise.all(selectedIds.map(id => AvailabilityService.requestChange(id, 'delete')))
		const failed = results.some(result => !result.success)
		setDeleting(false)
		setSelectedIds([])

		if (failed) {
			await Swal.fire(
				t('notifications.errorTitle'),
				t('trainingAvailability.list.deleteSelectedFailed'),
				'error',
			)
		} else {
			await Swal.fire(
				t('notifications.deletedTitle'),
				t('trainingAvailability.list.deletedSelectedSuccess'),
				'success',
			)
		}

		await loadData()
	}, [deleting, loadData, selectedIds, t])

	return {
		availability,
		availabilityError,
		dateDisplay,
		datePickerRef,
		deleting,
		handleAdd,
		handleAddForDate,
		handleDatePickerBlur,
		handleDatePickerChange,
		handleDeleteSelected,
		handleNext,
		handlePrevious,
		handleToday,
		handleToggleDatePicker,
		handleToggleSelect,
		inputRef,
		loading,
		selectedIds,
		showDatePicker,
		startDate,
		tempDate,
		projects,
		projectsError,
		trainings,
		trainingsError,
	}
}
